import type { FC } from 'react';
import React from 'react';
import { isHubsEnabledInHelpCenter, isJsmDestinationOpenRedirectFixEnabled } from '@helpcenter/feature-flags';
import { isCSMHelpCenter } from '@atlassian/help-center-common-util/advanced-help-center';
import { getEnv } from '@atlassian/help-center-common-util/env';
import { getRelativeBasePath } from '@atlassian/help-center-common-util/history';
import { assign, isValidLocationNew, isValidLocationOld, replace } from '@atlassian/help-center-common-util/location';
import Redirect from '@atlassian/help-center-common-util/redirect';
import {
    getHelpCenterUrl,
    parseLoginSignupRedirectUrlParams,
    convertAbsoluteUrlToRelativeUrl,
    getDefaultDestination,
} from '@atlassian/help-center-common-util/url';

export const RedirectLoggedInUser: FC<{
    portalId: string | undefined;
}> = ({ portalId }) => {
    const { destination } = parseLoginSignupRedirectUrlParams();

    if (isHubsEnabledInHelpCenter() && isCSMHelpCenter(getEnv().helpCenterType)) {
        const redirectPath = `${getRelativeBasePath()}${destination ? `/${destination}` : ''}`;
        replace(redirectPath);
        return null;
    }

    if (destination) {
        if (isJsmDestinationOpenRedirectFixEnabled()) {
            if (isValidLocationNew(destination)) {
                const relativeUrl = convertAbsoluteUrlToRelativeUrl(destination);
                assign(relativeUrl);
                return null;
            }

            if (decodeURIComponent(destination).startsWith('//')) {
                assign(getHelpCenterUrl());
                return null;
            }
        } else if (isValidLocationOld(destination)) {
            const relativeUrl = convertAbsoluteUrlToRelativeUrl(destination);
            assign(relativeUrl);
            return null;
        }
    }

    if (shouldExecuteFullPageReload(destination)) {
        // TypeScript upgrade (v4.4.3). Please correct when you revisit this code. Please correct when this code is revisited.
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        replace(`${getRelativeBasePath()}/${destination}`);
        return null;
    }

    const relativeDestination = `/${destination || getDefaultDestination(portalId)}`;
    return <Redirect to={relativeDestination} />;
};

const fullPageReloadPathsRegexs = [
    /^request\/[0-9]+\/attachment\/[0-9]+[/]?(\?.*)?$/,
    /^request\/[0-9]+\/attachment\/[0-9]+\/thumbnail[/]?(\?.*)?$/,
];

/**
 * Some URLs, even though they start with /servicedesk/customer, don't have any specific component mapped to that route.
 * When customers account log in the portal will simply do an SPA redirection and it will end up in 404
 * This method will allow for these "special" URLs to execute a full page reload after a customer account logs in.
 */
const shouldExecuteFullPageReload = (destination: string | undefined): boolean => {
    if (destination) {
        return fullPageReloadPathsRegexs.some((regex) => regex.test(destination));
    }
    return false;
};
